import type { DemoDoc } from '@mado/schema';
import { el } from './stage';
import type { Strings } from './strings';

/**
 * Lead capture (`DemoDoc.gate`): a form card over the stage when the viewer moves on from the
 * gate's step. The runtime decides when (`Player.gateDue`); this file draws the card, checks
 * it, and reports one answer: the values, or a skip.
 *
 *  - Rule 3: nothing is stored. "Answered" lives in this module for the page load, so a
 *    replay, a second Watch or a remount of the same demo on the same page does not ask
 *    again; a reload asks again.
 *  - The card is modal: a scrim over the whole stage (above the chrome bar and the CTA), focus
 *    kept inside, Escape skips when skipping is allowed. Input inside it never reaches the
 *    stage's gestures, keys or miss detector (`stop` below; the runtime's capture-phase
 *    takeover listeners skip `.mado-gate`).
 *  - Phones: inputs at 16px (iOS zooms the page into a smaller field), the scrim scrolls when
 *    the keyboard leaves it short, and nothing here is drawn inside the scaled stage (the iOS
 *    stage rules, docs/plans/demo-quality.md 2.1): no backdrop filter, no fading scrim.
 *  - The email check is deliberately plain; ingest checks again (`parseGateSubmit`).
 *    Lengths are capped here because the wire refuses a whole batch over 200 per value.
 */

type Gate = NonNullable<DemoDoc['gate']>;
export type GateField = Gate['fields'][number];

const answered = new Set<string>();
let uid = 0;

export const EMAIL_OK = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

const FIELD: Record<GateField, [type: string, auto: string, max: number]> = {
  email: ['email', 'email', 200],
  name: ['text', 'name', 120],
  company: ['text', 'organization', 120],
};

export function gateAnswered(demoId: string): boolean {
  return answered.has(demoId);
}

/** For tests: forget this page load's answers. */
export function resetGateAnswers(): void {
  answered.clear();
}

/**
 * Show the form in `host` (the viewport). `done` gets the trimmed values on submit, or null
 * on skip, once; the card is gone by then. Returns a function that removes it unanswered
 * (the player being destroyed).
 */
export function openGate(
  host: HTMLElement,
  gate: Gate,
  demoId: string,
  s: Strings,
  done: (values: Partial<Record<GateField, string>> | null) => void,
): () => void {
  const id = `mado-gate-${++uid}`;
  const scrim = el('div', 'mado-gate');
  const form = document.createElement('form');
  form.className = 'mado-gate-box';
  form.noValidate = true;
  form.tabIndex = -1;
  form.setAttribute('role', 'dialog');
  form.setAttribute('aria-modal', 'true');
  form.setAttribute('aria-labelledby', `${id}-t`);
  form.setAttribute('aria-describedby', `${id}-s`);

  const t = el('p', 'mado-card-t');
  t.id = `${id}-t`;
  t.textContent = s.gateTitle;
  const sub = el('p', 'mado-card-sub');
  sub.id = `${id}-s`;
  sub.textContent = s.gateSub;
  form.append(t, sub);

  const inputs: Array<[GateField, HTMLInputElement]> = [];
  for (const f of gate.fields) {
    const [type, auto, max] = FIELD[f];
    const lab = el('label', 'mado-gate-f');
    const span = el('span');
    span.textContent = f === 'email' ? s.fieldEmail : f === 'name' ? s.fieldName : s.fieldCompany;
    const input = document.createElement('input');
    input.type = type;
    input.name = f;
    input.setAttribute('autocomplete', auto);
    input.maxLength = max;
    input.required = true;
    if (f === 'email') {
      input.inputMode = 'email';
      input.spellcheck = false;
      input.autocapitalize = 'off';
    }
    lab.append(span, input);
    form.appendChild(lab);
    inputs.push([f, input]);
  }

  const err = el('p', 'mado-gate-err');
  err.id = `${id}-e`;
  err.setAttribute('aria-live', 'polite');
  const go = document.createElement('button');
  go.type = 'submit';
  go.className = 'mado-end-cta';
  go.textContent = s.gateContinue;
  const actions = el('div', 'mado-card-actions');
  actions.appendChild(go);
  let skip: HTMLButtonElement | null = null;
  if (gate.skippable !== false) {
    skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'mado-end-again mado-end-again--quiet';
    skip.textContent = s.gateSkip;
    actions.appendChild(skip);
  }
  // The customer collects this (they are the controller): say to whom, and link their policy.
  const note = el('p', 'mado-gate-note');
  note.textContent = s.sharedWith(gate.to?.trim() || null);
  if (gate.privacy) {
    const a = document.createElement('a');
    a.href = gate.privacy;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = s.privacy;
    note.append(' ', a);
  }
  form.append(err, actions, note);
  scrim.appendChild(form);

  // The stage's gestures, keys and miss detector listen on ancestors: nothing from here
  // reaches them (they would take a tap in a field for a miss, an arrow key for "back").
  const stop = (e: Event): void => e.stopPropagation();
  for (const type of ['pointerdown', 'pointermove', 'pointerup', 'click', 'wheel', 'keydown']) {
    scrim.addEventListener(type, stop);
  }

  const before = document.activeElement;
  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    scrim.remove();
    if (before instanceof HTMLElement && before.isConnected) {
      try {
        before.focus({ preventScroll: true });
      } catch {
        /* focus is a courtesy */
      }
    }
  };
  const finish = (values: Partial<Record<GateField, string>> | null): void => {
    if (closed) return;
    answered.add(demoId);
    close();
    done(values);
  };

  const fail = (input: HTMLInputElement, text: string): void => {
    for (const [, i] of inputs) i.removeAttribute('aria-invalid');
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', err.id);
    err.textContent = text;
    input.focus();
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const values: Partial<Record<GateField, string>> = {};
    for (const [f, input] of inputs) {
      const v = input.value.trim().slice(0, FIELD[f][2]);
      if (!v)
        return fail(
          input,
          f === 'email' ? s.enterEmail : f === 'name' ? s.enterName : s.enterCompany,
        );
      if (f === 'email' && !EMAIL_OK.test(v)) {
        return fail(input, s.badEmail);
      }
      values[f] = v;
    }
    finish(values);
  });
  skip?.addEventListener('click', () => finish(null));
  scrim.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && skip) {
      e.preventDefault();
      finish(null);
      return;
    }
    if (e.key !== 'Tab') return;
    // Keep focus in the card.
    const all = [...form.querySelectorAll<HTMLElement>('input,button,a')];
    const first = all[0];
    const last = all[all.length - 1];
    if (!first || !last) return;
    const a = document.activeElement;
    if (e.shiftKey && (a === first || a === form)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && a === last) {
      e.preventDefault();
      first.focus();
    }
  });

  host.appendChild(scrim);
  // The card itself, not the first field: a screen reader reads its name and line first, and a
  // phone does not throw a keyboard over the stage before the viewer chose to type.
  try {
    form.focus({ preventScroll: true });
  } catch {
    form.focus();
  }
  return close;
}
